import React, { FC } from 'react';
import styled from 'styled-components';

import { DataGridPremium } from '@clatter/ui';
import { useDeepCompareMemo } from '@clatter/platform';

import { GridColDef } from '@mui/x-data-grid-premium';
import { useGetCCMSTypesQuery } from '../../services/ccms.service';
import { countColumn, nameColumn, updatedAtColumn } from '../../helpers/dataGrid';

interface DataSectionProps {
  cmsScopeId: number;
  onFilterModelChange: any;
  apiRef: any;
  onChange: any;
  selectedType: any;
  entityPrimaryKey: string;
  selectedEntities: any[];
  columnsByTypes: any;
  typesData: any;
  isLoading?: boolean;
  urlPrefix: string;
}

const sortColumnsByKey = (columns: any[], idKey: string) => {
  return (columns || []).sort((a: any, b: any) => {
    if (a.field.includes(idKey) && !b.field.includes(idKey)) {
      return -1;
    } else if (!a.field.includes(idKey) && b.field.includes(idKey)) {
      return 1;
    } else {
      return a.field.localeCompare(b.field);
    }
  });
};

const StyledContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.large};

  overflow-x: scroll;
`;

export const EntitiesManager: FC<DataSectionProps> = ({
  selectedEntities,
  apiRef,
  selectedType,
  onFilterModelChange,
  columnsByTypes = {},
  typesData = [],
  onChange,
  isLoading: isParentLoading,
  cmsScopeId,
  entityPrimaryKey,
}) => {
  const { data: entitiesData, isFetching: isEntityDataLoading } = useGetCCMSTypesQuery(
    { url: `/cms-scopes/${cmsScopeId}/entities/${selectedType?.Name}` },
    {
      skip: !cmsScopeId || !selectedType?.Name,
      refetchOnMountOrArgChange: true,
    },
  );

  const isLoading = isParentLoading || isEntityDataLoading;

  const getTableData = useDeepCompareMemo(() => {
    const tableData: {
      columns: GridColDef[];
      rows: any[];
    } = {
      columns: [],
      rows: [],
    };

    if (!selectedType) {
      tableData.columns = [
        {
          ...nameColumn,
          field: 'DisplayName',
        },
        {
          ...countColumn,
        },
        {
          ...updatedAtColumn,
          field: 'LastUpdated',
          headerName: 'Last Updated',
        },
      ];

      tableData.rows = typesData || [];
    }

    if (selectedType && entitiesData && columnsByTypes) {
      const columnsByType = columnsByTypes[selectedType?.Name];

      if (!columnsByType) {
        const message = `Error getting columns for type: ${selectedType?.Name}`;
        console.error(message);
      }

      tableData.columns = columnsByType || [];

      const selectedSet = new Set(selectedEntities.map((item) => item[entityPrimaryKey]));

      const filteredEntities = entitiesData.filter((item) => !selectedSet.has(item.Data[entityPrimaryKey]));

      tableData.rows = filteredEntities.map((entity: any) => ({
        ID: entity.ID,
        ...(entity?.Data || {}),
      }));
    }

    return tableData;
  }, [typesData, selectedType, entitiesData, columnsByTypes, selectedEntities]);

  const handleRowClick = (data: any) => {
    if (isLoading) {
      return;
    }
    
    if (!selectedType) {
      onChange({ type: 'SET_TYPE', payload: { type: data?.row } });
      return;
    }

    onChange({ type: 'ADD', payload: { type: selectedType, entity: data.row } });
  };

  return (
    <StyledContainer>
      {/*@ts-ignore*/}
      <DataGridPremium
        name="workspace-template-data"
        getRowId={(row: any) => row?.ID}
        columns={getTableData.columns}
        rows={getTableData.rows}
        loading={isLoading}
        onRowClick={handleRowClick}
        apiRef={apiRef}
        dataGridProps={{
          onFilterModelChange: onFilterModelChange,
          rowHeight: 30,
          headerHeight: 30,
        }}
        disablePersist
        showVerticalSidebar
      />
    </StyledContainer>
  );
};
